import Layout from "components/layout";
import Submit from "components/submit";
import Filters from "components/pages/questionnaire/filters";
import Checkbox from "components/form/checkbox";
import Image from "next/image";

import * as questionnairesApi from "api/questionnaires";
import * as legacyStudyPlanApi from "api/legacy_study_plan";
import {
  questionPath,
  persistQuestionnaire,
  questionnaireCounters,
  filtersFromResponse
} from "utils/questionnaire";
import { filterObjByPresent } from "utils";
import { normalizeFilters } from "utils/metadata";
import { track } from "utils/tracker";
import clsx from "clsx";
import { resetScroll } from "utils/browser";

import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useNotifications } from "hooks/notifications";
import { useUser } from "hooks/user";
import { useMounted } from "hooks/react";

const defaultQuestionnaireSize = 10;

type QuestionnaireTemplateProps = {
  subject: { name: string; slug: string };
  topic: { name: string; slug: string };
  institution?: string;
  year?: number;
};

export default function QuestionnaireTemplate(
  props: QuestionnaireTemplateProps
): JSX.Element {
  const { subject, topic } = props;
  const filters = filterObjByPresent({ ...props });
  const { user } = useUser();

  const [showFilters, setShowFilters] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notAnswered, setNotAnswered] = useState(false);
  const { setNotification } = useNotifications();
  const router = useRouter();

  const disableQuestionnaireCreation = !!topic && !questions;

  useMounted(resetScroll);

  useEffect(() => {
    if (!topic) return;

    legacyStudyPlanApi.subtopics(subject.slug).then((response) => {
      const topicInfo = response.body.children.find(
        (child) => child.slug == topic.slug
      );
      setQuestions(topicInfo.questions);
    });
  }, [subject, topic]);

  function createQuestionnaire(overridingFilters = null) {
    if (hasAnsweredAllQuestions()) {
      return setNotification({
        title: "Acabaram as questões",
        text: "Você já respondeu todas as nossas questões desse tópico! 😀"
      });
    }

    setShowFilters(false);
    setLoading(true);

    const queryFilters = overridingFilters || { ...filters, notAnswered };
    return questionnairesApi.create(queryFilters).then((response) => {
      handleCreationResponse(response, queryFilters);
    });
  }

  function filtersText(filters) {
    if (JSON.stringify(filters) == "{}") return "randômico";

    const { institution, year } = filters;
    if (institution) return year ? `${institution} ${year}` : institution;

    const { subject, topic } = filters;
    return topic ? topic.name : subject.name;
  }

  function questionsCountText() {
    const { institution, year } = filters;
    if (institution && year) return "Todas as questões da prova";

    const totalCount = questionsCount();
    const count =
      totalCount > defaultQuestionnaireSize
        ? defaultQuestionnaireSize
        : totalCount;
    if (count === 1) return "1 questão";

    if (count === 0) return "Nenhuma questão";

    return `${count} questões ${
      count == defaultQuestionnaireSize ? "aleatórias" : ""
    }`;
  }

  function questionsCount() {
    if (!topic) return defaultQuestionnaireSize;
    if (!questions) return null;

    return notAnswered ? questions.total - questions.answered : questions.total;
  }

  function handleCreationResponse(response, filters) {
    const data = response.body.data;
    const question = data.answers.length > 0 ? data.answers[0].question : null;
    if (!question) return handleNotFound(filters);

    handleCreatedQuestionnaire(response);
  }

  function handleCreatedQuestionnaire(response) {
    const data = response.body.data;
    const newQuestionnaire = {
      id: data.id,
      page: 1,
      seriousFrom: data.seriousFrom,
      totalTime: data.totalTime,
      question: data.answers[0]?.question,
      ...filtersFromResponse(response),
      ...questionnaireCounters(response)
    };
    persistQuestionnaire(newQuestionnaire);
    router.push(questionPath(data.answers[0].question));
  }

  function handleNotFound(filters) {
    setLoading(false);
    track("Did not find questions", normalizeFilters(filters));
    setNotification({
      title: "Não encontramos questões",
      text: "Ainda não conseguimos atender os filtros selecionados :(",
      type: "error"
    });
  }

  function pageTitle() {
    return `Simulado ${filtersMetaText("do ENEM")
      .replace("de provas passadas ", "")
      .replace(" do ENEM e de outros vestibulares.", "")
      .replace(" e veja seus gráficos de desempenho.", "")}`;
  }

  function metaDescription() {
    return `Resolva gratuitamente questões ${filtersMetaText(
      "de provas passadas do ENEM e de outros vestibulares e veja seus gráficos de desempenho."
    )}`;
  }

  function filtersMetaText(defaultValue) {
    if (!filters) return defaultValue;

    let text = defaultValue;
    if (topic) {
      text = `de ${topic.name} (${subject.name}) de provas passadas do ENEM e de outros vestibulares.`;
    } else if (subject && subject.name) {
      text = `de ${subject.name} de provas passadas do ENEM e de outros vestibulares.`;
    } else if (filters.year) {
      if (filters.institution === "ENEM") {
        text = `do ENEM de ${filters.year} e veja seus gráficos de desempenho.`;
      } else {
        text = `da ${filters.institution} de ${filters.year} e veja seus gráficos de desempenho.`;
      }
    } else if (filters.institution) {
      if (filters.institution === "ENEM") {
        text = `de provas passadas do ENEM e veja seus gráficos de desempenho.`;
      } else {
        text = `de provas passadas da ${filters.institution} e veja seus gráficos de desempenho.`;
      }
    }
    return text;
  }

  function hasAnsweredAllQuestions() {
    if (questionsCount() > 0) return false;

    return questions.total === questions.answered;
  }

  return (
    <Layout title={pageTitle()} description={metaDescription()}>
      {showFilters && (
        <div className="md:pl-[240px] fixed z-40 left-0 top-0 flex justify-center w-full h-full box-border md:z-30 md:h-auto">
          <Filters
            className="md:ml-[50%] md:mt-[5%] md:w-[500px] md:max-h-[80vh] transform-none md:border md:border-gray-100 md:rounded-lg md:shadow-xl md:transform md:-translate-x-1/2"
            isClosable={true}
            onSearch={(filters) => {
              track("Searched questions", {
                ...normalizeFilters(filters),
                questionCount: filters.questionCount,
                maxQuestions: filters.maxQuestions,
                tweakedQuestionsCount: filters.tweakedQuestionsCount || false
              });
              createQuestionnaire(filters);
            }}
            onClose={() => setShowFilters(false)}
          />
        </div>
      )}
      <main className="max-h-[calc(100dvh-106px)] !min-h-0 overflow-clip">
        <div className="container">
          <div className="flex flex-col items-center md:mt-12">
            <h2 className="mb-4 text-center text-4xl leading-tight md:text-5xl">
              Simulado <span className="font-bold">{filtersText(filters)}</span>
              {filters.subject?.name && filters.topic?.name && (
                <span className="block mt-3 text-center text-emphasis-medium text-xl">
                  {filters.subject.name}
                </span>
              )}
            </h2>
            <p
              className={clsx(
                "fade-loading mt-0 text-emphasis-medium text-lg",
                { "loading w-48": !questionsCount() && questionsCount() !== 0 }
              )}
            >
              {questionsCountText()}
            </p>

            <div className="max-w-sm">
              <div className="actions flex flex-col items-center mt-4 w-full">
                {user && (
                  <div className="mb-4">
                    <Checkbox
                      label="Remover questões já respondidas"
                      onChange={(newValue) => setNotAnswered(newValue)}
                    />
                  </div>
                )}

                <Submit
                  onClick={() => createQuestionnaire()}
                  className="btn btn-primary w-full"
                  loading={loading}
                  disabled={disableQuestionnaireCreation}
                >
                  Resolver simulado
                </Submit>
                <button
                  onClick={() => setShowFilters(true)}
                  className="btn btn-secondary mt-3 w-full"
                >
                  Escolher outras questões
                </button>
              </div>
              <div className="mt-8">
                <Image
                  src="/images/garoto-usando-computador.png"
                  className="w-full"
                  alt="Garoto usando o computador"
                  width="800"
                  height="800"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}
